import { Auth0Client, type CacheLocation } from '@auth0/auth0-spa-js';

import { GroupType, type UserLoginInfo } from '@/helpers/Authentication/types';
import { logError } from '@/helpers/LogHelper';

import { Stage } from '@/types';
import { captureException } from '@sentry/browser';
import type { HgSupportedLanguage } from '@smg-real-estate/domus';
import { isPlatformHg } from '../platformDetection';

let auth0: Auth0Client;

// For internal usage by the auth helper functions
function getAuth0Client(): Auth0Client | undefined {
  // Low-level cryptographic operations are available only in secure contexts (HTTPS)
  if (!window.crypto?.subtle) {
    console.debug('window.crypto.subtle is not available');
    return;
  }

  if (!auth0) {
    auth0 = new Auth0Client({
      domain: import.meta.env.AUTH0_DOMAIN,
      clientId: import.meta.env.AUTH0_CLIENT_ID,
      authorizationParams: {
        audience: import.meta.env.AUTH0_AUDIENCE,
        redirect_uri: import.meta.env.AUTH0_REDIRECT_URL,
      },
      // We use localstorage only for ui-test purposes when ENV is set to 'cypress' and the in-memory cache everywhere else.
      cacheLocation: (import.meta.env.AUTH0_CACHE_LOCATION as CacheLocation) || 'memory',
      cookieDomain: import.meta.env.AUTH0_COOKIE_DOMAIN,
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      sessionCheckExpiryDays: 365, // Maximum session lifetime
    });
  }

  return auth0;
}

export async function retrieveRedirectUrlFromAppState(): Promise<string | undefined> {
  try {
    return (await getAuth0Client()?.handleRedirectCallback())?.appState?.redirectUrl;
  } catch (err) {
    captureException(err, {
      // Exception thrown is usually caused by user accessing the login-callback page directly with expired state query params or without any query params
      // It does not happen in normal user flow, we should log it as a warning
      level: 'warning',
    });
  }
}

export async function loginWithRedirect(preferredLanguage?: HgSupportedLanguage): Promise<void> {
  const language = preferredLanguage ?? 'de';

  await getAuth0Client()?.loginWithRedirect({
    authorizationParams: {
      redirect_uri: import.meta.env.AUTH0_REDIRECT_URL,
      referral_url: window.location.href,
      referring_feature: 'favourites',
      referring_platform: isPlatformHg() ? 'homegate_web' : 'immoscout24_web',
      ui_locales: language,
    },
    appState: {
      redirectUrl: window.location.href.includes('login-callback') ? '' : window.location.href,
    },
  });
}

export const fillCache = async (): Promise<void> => {
  const isTestOrProd =
    import.meta.env.VUE_APP_ENVIRONMENT === Stage.Test ||
    import.meta.env.VUE_APP_ENVIRONMENT === Stage.Prod;

  try {
    await (isTestOrProd ? getAuth0Client()?.checkSession() : getAuth0Client()?.getTokenSilently());
  } catch (err) {
    const isUnexpectedError = ['missing_refresh_token'].every(
      (expectedError) => err.error != expectedError,
    );

    if (isUnexpectedError) {
      logError('Unexpected error getting auth token', err.error);
    }
  }
};

const isUserLoggedIn = async (): Promise<boolean> => {
  await fillCache();

  return await getAuth0Client()?.isAuthenticated();
};

export const getAuthToken = async (): Promise<string | undefined> => {
  // Remove this check when Auth0 can work with MR environment
  if (import.meta.env.VUE_APP_STAGE === 'mr') {
    return undefined;
  }

  if (!(await isUserLoggedIn())) {
    return undefined;
  }

  try {
    return await getAuth0Client()?.getTokenSilently();
  } catch (err) {
    const isUnexpectedError = ['missing_refresh_token'].every(
      (expectedError) => err.error != expectedError,
    );

    if (isUnexpectedError) {
      logError('Unexpected error getting auth token', err.error);
    }
  }
};

export async function getUserLoginInfo(): Promise<UserLoginInfo | undefined> {
  if (!(await isUserLoggedIn())) {
    return undefined;
  }

  const user = await getAuth0Client()?.getUser();

  if (user) {
    return {
      username: user.preferred_username || '',
      email: user.email,
      name: user.name,
      picture: user.picture,
      groups: user['cognito:groups'] || [],
      userId: user.sub,
      loginProvider: user.sub?.split('|')[0],
    };
  }

  return undefined;
}

export async function isPrivateLoggedInUser(): Promise<boolean> {
  try {
    const user = await getUserLoginInfo();

    return user?.groups?.includes(GroupType.PRIVATE) || false;
  } catch (err) {
    throw new Error(err);
  }
}
